import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ProjectObject } from "@ServerTypes/projects/project.schema";

/**
 * FINDING — every slice below is created with a bare `initialState: []`, so each one's state type
 * is the untyped `never[]`, and `RootState["roleData"]` &c. are only as loose as that.
 *
 * The `: any` that used to sit on each exported reducer has been removed (it made those RootState
 * members plain `any`); `never[]` still absorbs every field read the consumers do, so nothing
 * outside this module had to change. Giving each slice a real element type
 * (`initialState: [] as ProjectRoleItem[]`, …) is the remaining work, and it has to be done one
 * slice at a time together with that slice's consumers.
 */

// Slice to store the list of roles within a project
const roleDataSlice = createSlice({
  name: "roleData",
  initialState: [],
  reducers: {
    setProjectRolesList: (_state, action) => action.payload,
  },
});
export const { setProjectRolesList } = roleDataSlice.actions;
export const roleData = roleDataSlice.reducer;

// Slice to store list of users inside spaces
const spacesUsersDataSlice = createSlice({
  name: "spacesUsersData",
  initialState: [],
  reducers: {
    setSpacesInProjectList: (_state, action) => action.payload,
  },
});
export const { setSpacesInProjectList } = spacesUsersDataSlice.actions;
export const spacesUsersData = spacesUsersDataSlice.reducer;

// Slice to store project entities list
const entityListSlice = createSlice({
  name: "entityList",
  initialState: [],
  reducers: {
    setEntityList: (_state, action) => action.payload,
  },
});
export const { setEntityList: setEntityListAction } = entityListSlice.actions;
export const setEntityList = entityListSlice.reducer;

// Slice to store status list
const statusDataSlice = createSlice({
  name: "statusData",
  initialState: [],
  reducers: {
    setStatusData: (_state, action) => action.payload,
  },
});
export const { setStatusData } = statusDataSlice.actions;
export const statusData = statusDataSlice.reducer;

// Slice to store category list
const categoryListSlice = createSlice({
  name: "categoryList",
  initialState: [],
  reducers: {
    setCategoryList: (_state, action) => action.payload,
  },
});
export const { setCategoryList: setCategoryListAction } = categoryListSlice.actions;
export const setCategoryList = categoryListSlice.reducer;

// Slice for deleted space status
const deleteSpaceSlice = createSlice({
  name: "deleteSpace",
  initialState: [],
  reducers: {
    deletedSpaceStatus: (_state, action) => action.payload,
  },
});
export const { deletedSpaceStatus } = deleteSpaceSlice.actions;
export const deleteSpace = deleteSpaceSlice.reducer;

// Slice for deleted user status
const deletedUserSlice = createSlice({
  name: "DeletedUserState",
  initialState: [],
  reducers: {
    deletedUserStatus: (_state, action) => action.payload,
  },
});
export const { deletedUserStatus } = deletedUserSlice.actions;
export const DeletedUserState = deletedUserSlice.reducer;

// Slice for project setting object.
// State shape is the server `ProjectObject` (from `@ServerTypes/projects/project.schema`),
// the single source of truth — returned by `GET /projects/getProjectObject/:id`.
const initialProjectSettingObjectState: ProjectObject = {};
const projectSettingObjectSlice = createSlice({
  name: "getProjectSettingObject",
  initialState: initialProjectSettingObjectState,
  reducers: {
    getProjectSettingObject: (_state: ProjectObject, action: PayloadAction<ProjectObject | undefined>) =>
      action.payload ?? {},
  },
});

const projectSideBarIconAndNameMapper = createSlice({
  name: "projectSideBarIconsAndNameMapper",
  initialState: [],
  reducers: {
    setProjectSideBarIconsAndNameMapper: (_state, action) => action.payload,
  },
});
export const { setProjectSideBarIconsAndNameMapper } = projectSideBarIconAndNameMapper.actions;
export const projectSideBarIconAndNameMapperReducer = projectSideBarIconAndNameMapper.reducer;
export const { getProjectSettingObject: getProjectSettingObjectAction } = projectSettingObjectSlice.actions;
export const getProjectSettingObject = projectSettingObjectSlice.reducer;
